import { Grid, InputAdornment, Typography } from '@material-ui/core';
import { DatePayload, formatDate, parseDate } from '@superdispatch/dates';
import {
  FormikCheckboxField,
  FormikCurrencyField,
  FormikMaxLengthTextField,
  FormikTextField,
} from '@superdispatch/forms';
import { useConstant } from '@superdispatch/hooks';
import { ColorDynamic } from '@superdispatch/ui';
import { TextBox } from '@superdispatch/ui-lab';
import { useFormikContext } from 'formik';
import { round } from 'es-toolkit/compat';
import { DateTime } from 'luxon';
import { formatCurrency } from 'shared/helpers/IntlHelpers';
import {
  convertPricePerKmToPricePerMile,
  kmToMile,
} from 'shared/modules/loadboard/LoadboardUtils';
import { WebViewLink } from 'shared/routing/WebViewLinks';
import { useCarrierSettings } from 'shared/settings/CarrierSettingsAPI';
import { useFlag } from 'shared/settings/FeatureToggles';
import styled from 'styled-components';
import {
  getBookingTimeRange,
  PostingLoadDTO,
} from '../../data/dto/PostingLoadDTO';
import {
  CARRIER_COMMENT_MAX_LENGTH,
  PostingRequestValuesDTO,
} from '../../data/dto/PostingRequestDTO';
import { PostingDateField } from './PostingDateField';
import { PostingLargestTrailerCapacityField } from './PostingLargestTrailerCapacityField';
import { PostingTermsLink } from './PostingTermsLink';
import { STEP_NAMES, StepKey } from './postingDateRules';
import { RequestDateField } from './RequestDateField';

const BookableBox = styled.div`
  display: inline-block;
  height: 20px;
  width: 20px;
  padding: 2px;
  background-color: ${ColorDynamic.Blue50};
  border-radius: 2px;
  margin-right: 4px;
`;

const Styled1 = styled.div`
  display: flex;
  align-items: center;
`;

const CommentTextField = styled(FormikMaxLengthTextField)`
  ${({ theme }) => theme.breakpoints.down('xs')} {
    .MuiOutlinedInput-multiline {
      padding: 10px 12px;
    }
  }
`;

function PostingDateFieldFooter() {
  return (
    <Styled1>
      <BookableBox />

      <Typography color="textSecondary" display="inline">
        — Preferred dates by shipper
      </Typography>
    </Styled1>
  );
}

function calculatePricePerMile(price: number, load: PostingLoadDTO) {
  if (load.distance_meters === 0) {
    return 0;
  }

  if (load.distance_meters == null) {
    return round(price / load.vehicles.length, 2);
  }

  const distanceInMiles = kmToMile(load.distance_meters / 1000);
  return round(price / distanceInMiles / load.vehicles.length, 2);
}

interface PricePerMileProps {
  price?: number;
  load: PostingLoadDTO;
}

function PricePerMile({ price = 0, load }: PricePerMileProps) {
  const pricePerMile =
    load.price_per_km && convertPricePerKmToPricePerMile(load.price_per_km);
  const nextPricePerMile =
    // We don't want to recalculate price per mile if base price wasn't changed.
    price === load.price && pricePerMile != null
      ? pricePerMile
      : calculatePricePerMile(price, load);

  return (
    <Typography aria-label="price per mile" color="textSecondary" noWrap={true}>
      {formatCurrency(nextPricePerMile, { minimumFractionDigits: 2 })} / mi
    </Typography>
  );
}

interface RequestFormFieldsProps {
  load: PostingLoadDTO;
  isEditMode?: boolean;
  disabled?: boolean;
  isBooking?: boolean;
}

export function RequestFormFields({
  load,
  isEditMode,
  disabled,
  isBooking,
}: RequestFormFieldsProps) {
  const { data: settings } = useCarrierSettings();
  const hasDateRules = useFlag('slb_load_details_dates');
  const { values, status } = useFormikContext<PostingRequestValuesDTO>();

  function renderDateField(stepKey: StepKey) {
    const fieldProps = {
      disabled,
      fullWidth: true,
      name: `${stepKey}_date` as const,
      label: `${STEP_NAMES[stepKey]} Date`,
      validate: validateLoadStepDateField(stepKey),
    };

    if (hasDateRules) {
      return (
        <RequestDateField
          {...fieldProps}
          stepKey={stepKey}
          load={load}
          isBooking={!!isBooking}
        />
      );
    }

    return (
      <PostingDateField
        {...fieldProps}
        preferredRange={getBookingTimeRange(load[stepKey])}
        renderFooter={() => <PostingDateFieldFooter />}
      />
    );
  }

  //useConstant used here to make sure
  // Largest Trailer Capacity field will be shown
  // until posting will be closed
  const shouldShowTrailerCapacity = useConstant(
    () => !isEditMode && !settings?.carrier.largest_trailer_capacity,
  );

  function validateLoadStepDateField(type: 'pickup' | 'delivery') {
    const loadStep = load[type];
    const isExactDate = loadStep.date_type === 'exact';
    const isNotEarlierDate = loadStep.date_type === 'not_earlier_than';
    const isNotLaterDate = loadStep.date_type === 'not_later_than';
    const loadStepDate = parseDate(loadStep.scheduled_at);
    const isPickupExpired =
      parseDate(load.pickup.scheduled_at) < DateTime.local().startOf('day');

    return (carrierVenueDate: DatePayload): string | void => {
      const carrierRequestedDate = carrierVenueDate.dateValue;
      const shortDate = formatDate(loadStepDate, { variant: 'ShortDate' });
      const startOfToday = DateTime.local().startOf('day');

      if (carrierRequestedDate < startOfToday) {
        return 'Date cannot be in the past';
      }

      if (
        isNotEarlierDate &&
        carrierRequestedDate < loadStepDate.startOf('day')
      ) {
        return `Date must be on or after ${shortDate}`;
      }

      if (isBooking || !isPickupExpired) {
        if (
          isExactDate &&
          (carrierRequestedDate < loadStepDate.startOf('day') ||
            loadStepDate.endOf('day') < carrierRequestedDate)
        ) {
          return `Date must be ${shortDate}`;
        }

        if (
          isNotLaterDate &&
          loadStepDate.endOf('day') < carrierRequestedDate
        ) {
          return `Date must be on or before ${shortDate}`;
        }
      }
    };
  }

  return (
    <Grid container={true} spacing={2}>
      <Grid item={true} xs={12}>
        <PostingTermsLink load={load} />
      </Grid>

      <Grid item={true} xs={12}>
        <FormikCheckboxField
          disabled={isEditMode}
          name="$is_agree"
          label="I have read and agree with above T&C."
        />
      </Grid>

      {shouldShowTrailerCapacity && (
        <Grid item={true} xs={12}>
          <PostingLargestTrailerCapacityField />
        </Grid>
      )}

      <Grid item={true} xs={12}>
        <FormikTextField
          disabled={disabled}
          name="carrier_contact_name"
          label={<OptionalFieldLabel label="Contact Name" />}
          fullWidth={true}
        />
      </Grid>

      <Grid item={true} xs={12}>
        <CommentTextField
          disabled={disabled || isBooking}
          name="carrier_comment"
          label={<OptionalFieldLabel label="Comment" />}
          placeholder={
            isBooking ? "Comments aren't available with Book Now" : undefined
          }
          maxLength={CARRIER_COMMENT_MAX_LENGTH}
          fullWidth={true}
          multiline={true}
          minRows={1}
        />
      </Grid>

      <Grid item={true} xs={6}>
        {renderDateField('pickup')}
      </Grid>

      <Grid item={true} xs={6}>
        {renderDateField('delivery')}
      </Grid>

      <Grid item={true} xs={12}>
        <FormikCurrencyField
          disabled={disabled}
          name="price"
          label="Price"
          fullWidth={true}
          inputProps={{ decimalScale: 2, maxLength: 12 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <PricePerMile price={values.price} load={load} />
              </InputAdornment>
            ),
          }}
        />

        <Typography color="textSecondary">
          To place a bid, change the price
        </Typography>
      </Grid>

      {status.type === 'rejected' && (
        <Grid item={true} xs={12}>
          <Typography color="error">
            {status.payload.type !== 'CARRIER_IN_BLOCK_LIST' ? (
              status.payload.message
            ) : (
              <>
                Unable to request. Contact Shipper or{' '}
                <WebViewLink href="https://support.superdispatch.com/hc/articles/35646552969875">
                  learn more
                </WebViewLink>
                .
              </>
            )}
          </Typography>
        </Grid>
      )}
    </Grid>
  );
}

interface OptionalFieldLabelProps {
  label: string;
}

function OptionalFieldLabel({ label }: OptionalFieldLabelProps) {
  return (
    <>
      {label} <TextBox color="secondary">(optional)</TextBox>
    </>
  );
}
